import React, { useMemo } from 'react'
import { Button, Flex, Table, Text, useAppTheme } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { Pencil, Trash2 } from 'lucide-react'
import { AccordionCard } from 'components/AccordionCard'
import { useRuleDetailContextValue } from '../context/RuleDetailProvider'
import { useRuleDetailWorkflowsContextValue } from '../context/RuleDetailWorkflowsProvider'
import { useRuleQuery } from 'queries/wage/useRuleQuery'
import { formatNumberToCurrency } from 'lib/number'

type Rate = NonNullable<
  ReturnType<typeof useRuleQuery>['data']
>['rates'][number]

export const RatesSection = () => {
  const { space } = useAppTheme()
  const {
    state: { rates },
  } = useRuleDetailContextValue()

  const { addRateFlow, editRateFlow, deleteRateConfirmFlow } =
    useRuleDetailWorkflowsContextValue()

  const columns = useMemo<ColumnDef<Rate>[]>(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        size: 80,
      },
      {
        header: 'Rate ($/hr)',
        accessorKey: 'hourlyRateCents',
        size: 120,
        cell: ({ row }) =>
          formatNumberToCurrency(row.original.hourlyRateCents / 100),
      },
      {
        header: 'Note',
        accessorKey: 'note',
        cell: ({ row }) =>
          row.original.note ?? <Text color="lightText">—</Text>,
      },
      {
        header: 'Enabled',
        accessorKey: 'isEnabled',
        size: 100,
        cell: ({ row }) => (
          <Text color={row.original.isEnabled ? 'success' : 'lightText'}>
            {row.original.isEnabled ? 'Yes' : 'No'}
          </Text>
        ),
      },
      {
        header: '',
        id: 'actions',
        size: 80,
        cell: ({ row }) => (
          <Flex gap={1}>
            <Button
              variant="text"
              kind="small"
              style={{ padding: space[1] }}
              onClick={() => editRateFlow.open(row.original)}
            >
              <Pencil size={14} />
            </Button>
            <Button
              variant="text"
              kind="small"
              style={{ padding: space[1] }}
              onClick={() => deleteRateConfirmFlow.open(row.original)}
            >
              <Trash2 size={14} />
            </Button>
          </Flex>
        ),
      },
    ],
    [editRateFlow, deleteRateConfirmFlow]
  )

  return (
    <AccordionCard
      title={
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          width="100%"
        >
          <Text fontSize={2} color="lightText">
            Rates ({rates.length})
          </Text>
          <Button variant="secondary" kind="small" onClick={addRateFlow.open}>
            Add rate
          </Button>
        </Flex>
      }
    >
      {rates.length === 0 ? (
        <Flex justifyContent="center" p={3}>
          <Text color="lightText">No rates defined.</Text>
        </Flex>
      ) : (
        <Table columns={columns} data={rates} textVerticalAlign="middle" />
      )}
    </AccordionCard>
  )
}
